export type AssistantRenderMode = 'markdown' | 'plainText';

type BlockDelimiter = '$' | '$$' | '\\(' | '\\[' | '```' | '`';

type OpenBlock = {
  delimiter: BlockDelimiter;
  openIndex: number;
  braceDepth: number;
  parenDepth: number;
  bracketDepth: number;
};

export interface MathStreamRenderState {
  content: string;
  renderMode: AssistantRenderMode;
  shouldHold: boolean;
  issues: string[];
}

const LATEX_COMMAND_PATTERN = /\\(?:alpha|beta|gamma|delta|theta|lambda|mu|pi|sigma|phi|omega|sin|cos|tan|sec|csc|cot|sinh|cosh|tanh|log|ln|sqrt|frac|int|sum|prod|lim|left|right|cdot|times|div|pm|leq|geq|neq|approx|infty)\b/;

const isEscaped = (text: string, index: number): boolean => {
  let slashCount = 0;
  for (let cursor = index - 1; cursor >= 0 && text[cursor] === '\\'; cursor -= 1) {
    slashCount += 1;
  }
  return slashCount % 2 === 1;
};

const startsMathDelimiter = (text: string, index: number): BlockDelimiter | null => {
  if (text.startsWith('```', index)) {
    return '```';
  }

  if (text[index] === '`') {
    return '`';
  }

  if (text.startsWith('\\[', index)) {
    return '\\[';
  }

  if (text.startsWith('\\(', index)) {
    return '\\(';
  }

  if (text.startsWith('$$', index) && !isEscaped(text, index)) {
    return '$$';
  }

  if (
    text[index] === '$' &&
    !isEscaped(text, index) &&
    text[index + 1] !== '$' &&
    text[index - 1] !== '$'
  ) {
    return '$';
  }

  return null;
};

const isClosingDelimiter = (text: string, index: number, delimiter: BlockDelimiter): boolean => {
  switch (delimiter) {
    case '```':
      return text.startsWith('```', index);
    case '`':
      return text[index] === '`';
    case '\\[':
      return text.startsWith('\\]', index);
    case '\\(':
      return text.startsWith('\\)', index);
    case '$$':
      return text.startsWith('$$', index) && !isEscaped(text, index);
    case '$':
      return (
        text[index] === '$' &&
        !isEscaped(text, index) &&
        text[index + 1] !== '$' &&
        text[index - 1] !== '$'
      );
    default:
      return false;
  }
};

const closingLength = (delimiter: BlockDelimiter): number => {
  switch (delimiter) {
    case '```':
    case '\\[':
    case '\\(':
    case '$$':
      return 2 + Number(delimiter === '```');
    case '`':
    case '$':
      return 1;
    default:
      return 1;
  }
};

const isMathDelimiter = (delimiter: BlockDelimiter): boolean =>
  delimiter === '$' || delimiter === '$$' || delimiter === '\\(' || delimiter === '\\[';

const updateMathDepths = (block: OpenBlock, text: string, index: number): void => {
  if (isEscaped(text, index)) {
    return;
  }

  const char = text[index];
  if (char === '{') {
    block.braceDepth += 1;
  } else if (char === '}') {
    block.braceDepth = Math.max(0, block.braceDepth - 1);
  } else if (char === '(') {
    block.parenDepth += 1;
  } else if (char === ')') {
    block.parenDepth = Math.max(0, block.parenDepth - 1);
  } else if (char === '[') {
    block.bracketDepth += 1;
  } else if (char === ']') {
    block.bracketDepth = Math.max(0, block.bracketDepth - 1);
  }
};

const hasBalancedMathDepth = (block: OpenBlock): boolean =>
  block.braceDepth === 0 && block.parenDepth === 0 && block.bracketDepth === 0;

const hasDanglingLatexCommand = (text: string): boolean => /\\[a-zA-Z]*$/.test(text.trim());

const getBareMathLineState = (text: string): { startIndex: number; issues: string[] } | null => {
  const lastLineBreak = text.lastIndexOf('\n');
  const startIndex = lastLineBreak + 1;
  const line = text.slice(startIndex);
  const trimmed = line.trim();

  if (!trimmed || trimmed.includes('$') || trimmed.startsWith('```')) {
    return null;
  }

  if (!LATEX_COMMAND_PATTERN.test(trimmed)) {
    return null;
  }

  const hasMathSignal = /(?:=|\\int|\\sum|\\prod|\\lim|\\frac|\\sqrt|[_^]|\\,|\\cdot|\\times|\\div|[(){}[\]])/.test(trimmed);
  if (!hasMathSignal) {
    return null;
  }

  let braceDepth = 0;
  let parenDepth = 0;
  let bracketDepth = 0;

  for (let index = 0; index < line.length; index += 1) {
    if (isEscaped(line, index)) {
      continue;
    }

    const char = line[index];
    if (char === '{') {
      braceDepth += 1;
    } else if (char === '}') {
      braceDepth = Math.max(0, braceDepth - 1);
    } else if (char === '(') {
      parenDepth += 1;
    } else if (char === ')') {
      parenDepth = Math.max(0, parenDepth - 1);
    } else if (char === '[') {
      bracketDepth += 1;
    } else if (char === ']') {
      bracketDepth = Math.max(0, bracketDepth - 1);
    }
  }

  const issues: string[] = [];
  if (braceDepth > 0 || parenDepth > 0 || bracketDepth > 0) {
    issues.push('Unbalanced standalone math line');
  }
  if (hasDanglingLatexCommand(line)) {
    issues.push('Dangling LaTeX command');
  }

  return issues.length > 0 ? { startIndex, issues } : null;
};

export const getMathStreamRenderState = (
  rawContent: string,
  options?: { final?: boolean }
): MathStreamRenderState => {
  const final = Boolean(options?.final);
  const issues: string[] = [];
  let safeIndex = 0;
  let index = 0;
  let openBlock: OpenBlock | null = null;

  while (index < rawContent.length) {
    if (!openBlock) {
      const delimiter = startsMathDelimiter(rawContent, index);
      if (delimiter) {
        openBlock = {
          delimiter,
          openIndex: index,
          braceDepth: 0,
          parenDepth: 0,
          bracketDepth: 0,
        };
        index += delimiter.length;
        continue;
      }

      safeIndex = index + 1;
      index += 1;
      continue;
    }

    if (
      isClosingDelimiter(rawContent, index, openBlock.delimiter) &&
      (!isMathDelimiter(openBlock.delimiter) || hasBalancedMathDepth(openBlock))
    ) {
      index += closingLength(openBlock.delimiter);
      safeIndex = index;
      openBlock = null;
      continue;
    }

    if (isMathDelimiter(openBlock.delimiter)) {
      updateMathDepths(openBlock, rawContent, index);
    }

    index += 1;
  }

  if (openBlock) {
    const label = isMathDelimiter(openBlock.delimiter) ? 'math block' : 'code block';
    issues.push(`Incomplete ${label}`);
  }

  if (!openBlock && hasDanglingLatexCommand(rawContent)) {
    issues.push('Dangling LaTeX command');
  }

  const bareMathLineState = !openBlock ? getBareMathLineState(rawContent) : null;
  if (bareMathLineState) {
    safeIndex = Math.min(safeIndex, bareMathLineState.startIndex);
    issues.push(...bareMathLineState.issues);
  }

  if (!final) {
    return {
      content: rawContent.slice(0, safeIndex),
      renderMode: 'markdown',
      shouldHold: Boolean(openBlock) || Boolean(bareMathLineState),
      issues,
    };
  }

  if (issues.length > 0) {
    return {
      content: rawContent,
      renderMode: 'plainText',
      shouldHold: false,
      issues,
    };
  }

  return {
    content: rawContent,
    renderMode: 'markdown',
    shouldHold: false,
    issues: [],
  };
};
